import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatDateDE, formatErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge, CategoryBadge } from "@/components/Badges";
import UpdateFormDialog from "@/components/UpdateFormDialog";
import AnlagenManagerDialog from "@/components/AnlagenManagerDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Settings2, Loader2, ExternalLink, Factory } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [updates, setUpdates] = useState([]);
  const [anlagen, setAnlagen] = useState([]);
  const [busy, setBusy] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [anlagenOpen, setAnlagenOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadAll = async () => {
    setBusy(true);
    try {
      const [{ data: u }, { data: a }] = await Promise.all([
        api.get("/updates"),
        api.get("/anlagen"),
      ]);
      setUpdates(u);
      setAnlagen(a);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Fehler beim Laden");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user && user !== false) loadAll();
  }, [user]);

  if (loading) return null;
  if (!user || user === false) return <Navigate to="/admin/login" replace />;

  const onCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const onEdit = (u) => {
    setEditing(u);
    setFormOpen(true);
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/updates/${confirmDelete.id}`);
      toast.success("Update gelöscht");
      setConfirmDelete(null);
      loadAll();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Fehler beim Löschen");
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Admin</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Updates verwalten
            </h1>
            <p className="text-slate-600 mt-1">Updates erstellen, bearbeiten und Anlagen pflegen.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setAnlagenOpen(true)}
              className="border-slate-300"
              data-testid="manage-anlagen-button"
            >
              <Factory className="h-4 w-4 mr-2" />
              Anlagen verwalten
            </Button>
            <Button
              onClick={onCreate}
              className="bg-[#E2001A] hover:bg-[#C20015] text-white"
              data-testid="create-update-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Neues Update
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Updates gesamt" value={updates.length} testId="stat-total" />
          <StatCard
            label="Aktiv"
            value={updates.filter((u) => u.status === "aktiv").length}
            accent="text-emerald-600"
            testId="stat-aktiv"
          />
          <StatCard
            label="Geplant"
            value={updates.filter((u) => u.status === "geplant").length}
            accent="text-blue-600"
            testId="stat-geplant"
          />
          <StatCard label="Anlagen" value={anlagen.length} testId="stat-anlagen" />
        </div>

        {/* List */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {busy ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="h-6 w-6 mr-2 animate-spin" />
              Wird geladen…
            </div>
          ) : updates.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="font-heading text-xl font-semibold text-slate-900">Noch keine Updates</h3>
              <p className="text-sm text-slate-500 mt-2">Erstellen Sie das erste Update.</p>
              <Button
                onClick={onCreate}
                className="mt-4 bg-[#E2001A] hover:bg-[#C20015] text-white"
                data-testid="empty-create-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Neues Update
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {updates.map((u) => (
                <li
                  key={u.id}
                  className="p-5 flex flex-col md:flex-row md:items-center gap-4 hover:bg-slate-50/50"
                  data-testid={`admin-update-row-${u.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {formatDateDE(u.date)}
                      </span>
                      <StatusBadge status={u.status} />
                      <CategoryBadge category={u.category} />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 truncate">
                      {u.title}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-1">{u.description}</p>
                    {u.anlagen && u.anlagen.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {u.anlagen.map((a) => (
                          <span
                            key={a.id}
                            className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
                          >
                            {a.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                    <Link to={`/updates/${u.id}`} target="_blank">
                      <Button variant="ghost" size="sm" data-testid={`view-update-${u.id}`}>
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(u)}
                      className="border-slate-300"
                      data-testid={`edit-update-${u.id}`}
                    >
                      <Pencil className="h-4 w-4 mr-1.5" />
                      Bearbeiten
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmDelete(u)}
                      className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                      data-testid={`delete-update-${u.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <UpdateFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        anlagen={anlagen}
        onSaved={loadAll}
        onAnlagenChanged={loadAll}
      />

      <AnlagenManagerDialog
        open={anlagenOpen}
        onOpenChange={setAnlagenOpen}
        anlagen={anlagen}
        onChanged={loadAll}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              „{confirmDelete?.title}" wird unwiderruflich gelöscht. Zugehörige Dateien werden ebenfalls entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-[#E2001A] hover:bg-[#C20015] text-white"
              data-testid="confirm-delete"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, accent = "text-slate-900", testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testId}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-heading text-3xl font-bold mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
